import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../lib/api';

// Preserve the published article identity; accept this observed external-link spelling.
const ARTICLE_ALIASES = new Map([
  ['eksagoges-pente-vimata-nea-agora', 'eksagwges-pente-vimata-nea-agora'],
]);

export function useArticle(slug) {
  const articleSlug = ARTICLE_ALIASES.get(slug) || slug;
  const [run, setRun] = useState(0);
  const [state, setState] = useState({ key: slug, status: 'loading', article: null });

  useEffect(() => {
    let active = true;
    let retryTimer;
    const controller = new AbortController();
    const update = (status, article = null) => {
      if (active) setState({ key: slug, status, article });
    };
    update('loading');

    const load = async (attempt = 0) => {
      try {
        const { data } = await axios.get(`${API}/insights/articles/${encodeURIComponent(articleSlug)}`, {
          signal: controller.signal,
          timeout: 30000,
        });
        if (!active) return;
        if (data?.slug !== articleSlug || typeof data.title !== 'string' || typeof data.body !== 'string') {
          update('error');
          return;
        }
        update('ready', data);
      } catch (error) {
        if (!active || controller.signal.aborted || axios.isCancel(error)) return;
        const status = error.response?.status;
        if (status === 404) {
          update('not-found');
          return;
        }
        const transient = !status || status === 408 || status === 429 || status >= 500;
        if (transient && attempt === 0) {
          retryTimer = setTimeout(() => load(1), 1000);
        } else update('error');
      }
    };
    load();
    return () => {
      active = false;
      controller.abort();
      clearTimeout(retryTimer);
    };
  }, [slug, articleSlug, run]);

  // Do not briefly expose the previous article while the next route effect starts.
  const current = state.key === slug ? state : { status: 'loading', article: null };
  return { ...current, articleSlug, retry: () => setRun(value => value + 1) };
}
